import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Camp } from '../../utils/types';
import { useCreateCampDate } from '../../api/organizerAdmin';
import './CampDateModal.css';

interface CampDateModalProps {
  camp: Camp;
  organizerId: string;
  onClose: () => void;
}

const CampDateModal: React.FC<CampDateModalProps> = ({ camp, organizerId, onClose }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [capacity, setCapacity] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createCampDate = useCreateCampDate(organizerId);

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    if (!endDate || endDate < value) {
      setEndDate(value);
    }
  };

  const handleSave = async () => {
    if (!startDate) {
      setError('Start date is required.');
      return;
    }
    if (endDate && endDate < startDate) {
      setError('End date cannot be before start date.');
      return;
    }

    setError(null);
    try {
      await createCampDate.mutateAsync({
        campId: camp.id,
        data: {
          start_date: startDate,
          end_date: endDate || startDate,
          max_capacity: capacity ? parseInt(capacity, 10) : null,
        },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create camp date.');
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const isSingleDay = !endDate || endDate === startDate;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="camp-date-modal">
        <button className="modal-close-btn" onClick={onClose} disabled={createCampDate.isPending}>
          <X />
        </button>

        <h2 className="camp-date-modal__title">Add Camp Date</h2>
        <p className="camp-date-modal__subtitle">{camp.name}</p>

        {error && <div className="message message--error">{error}</div>}

        <div className="camp-date-modal__form">
          <div className="camp-date-modal__date-row">
            <div className="form-group">
              <label htmlFor="start-date">Start date</label>
              <input
                id="start-date"
                type="date"
                className="form-input"
                value={startDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                disabled={createCampDate.isPending}
              />
            </div>
            <div className="form-group">
              <label htmlFor="end-date">
                End date (optional)
              </label>
              <input
                id="end-date"
                type="date"
                className="form-input"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={createCampDate.isPending}
              />
            </div>
            <div className="form-group">
              <label htmlFor="capacity">
                Capacity
              </label>
              <input
                id="capacity"
                type="number"
                className="form-input"
                value={capacity}
                min="1"
                onChange={(e) => setCapacity(e.target.value)}
                disabled={createCampDate.isPending}
                placeholder="∞"
              />
            </div>
          </div>
        </div>

        <div className="camp-date-modal__actions">
          <button className="btn btn--secondary btn--md" onClick={onClose} disabled={createCampDate.isPending}>
            Cancel
          </button>
          <button className="btn btn--primary btn--md" onClick={handleSave} disabled={createCampDate.isPending}>
            {createCampDate.isPending ? 'Saving...' : 'Add Date'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampDateModal;
